import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings, Palette, Image, Grid, ShoppingCart, Camera, MessageCircle, Save, RotateCcw, Package, Eye, Upload, Boxes } from 'lucide-react';
import { API } from '../lib/api';

const inputClass = "w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500";
const card = "bg-white border border-gray-200 rounded-2xl p-5";
const sectionTitle = "text-base font-bold text-gray-900 mb-4 flex items-center space-x-2";
const labelClass = "block text-[11px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wider";

const ImageUploadField = ({ label, field, value, set, spec, previewClass }) => {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Selecione uma imagem'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Imagem deve ter no maximo 5MB'); return; }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      set(field, data.url);
      toast.success('Imagem enviada!');
    } catch (err) {
      toast.error('Erro ao enviar imagem');
    } finally { setUploading(false); }
  };

  return (
    <div>
      <label className={labelClass}>{label}</label>
      <div className="flex items-center gap-2 mb-1.5">
        <input type="text" value={value || ''} onChange={(e) => set(field, e.target.value)} placeholder="URL da imagem ou use o upload" className={`${inputClass} flex-1`} />
        <label className={`flex items-center space-x-1.5 px-3 py-2.5 rounded-lg text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap ${uploading ? 'bg-gray-100 text-gray-400' : 'bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-200'}`}>
          <Upload size={16} />
          <span>{uploading ? 'Enviando...' : 'Upload'}</span>
          <input type="file" accept="image/*" onChange={handleUpload} className="hidden" disabled={uploading} data-testid={`upload-${field}`} />
        </label>
      </div>
      <p className="text-[10px] text-gray-400 mb-2">{spec}</p>
      {value && <img src={value} alt={label} className={`${previewClass} mt-1 rounded-lg border border-gray-100`} />}
    </div>
  );
};

const Toggle = ({ checked, onChange, label, description }) => (
  <label className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-xl hover:border-teal-300 transition-colors cursor-pointer">
    <div>
      <p className="text-sm font-medium text-gray-900">{label}</p>
      {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
    </div>
    <div className="relative">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only peer" />
      <div className="w-10 h-5 bg-gray-300 rounded-full peer peer-checked:bg-teal-600 peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
    </div>
  </label>
);

const ColorField = ({ label, field, value, set }) => (
  <div>
    <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase tracking-wider">{label}</label>
    <div className="flex items-center space-x-2">
      <input type="color" value={value || '#000000'} onChange={(e) => set(field, e.target.value)} className="w-9 h-9 rounded-lg border border-gray-200 bg-white cursor-pointer flex-shrink-0" data-testid={`color-${field}`} />
      <input type="text" value={value || ''} onChange={(e) => set(field, e.target.value)} className={`${inputClass} flex-1 !py-1.5 !text-xs`} />
    </div>
  </div>
);

// iter77de: dropdown reusavel de filiais do tenant (usado em Regras do Catalogo).
// iter77df: catalogo SEMPRE precisa de uma filial regente — nao ha opcao vazia.
// Se `value` estiver vazio, auto-seleciona a primeira filial da lista.
const CatalogCompanySelect = ({ value, onChange }) => {
  const [companies, setCompanies] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/erp/companies`, { withCredentials: true });
        const list = data.data || [];
        setCompanies(list);
        // Se nao ha filial regente ainda, auto-seleciona a primeira ativa
        if (!value && list.length > 0) {
          const first = list.find(c => c.active !== false) || list[0];
          if (first?.id) onChange(first.id);
        }
      } catch { /* ERP may not be enabled */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} className={inputClass} data-testid="catalog-company-select" required>
      {!value && <option value="" disabled>— Selecione a filial regente —</option>}
      {companies.map(c => (
        <option key={c.id} value={c.id}>{c.code} | {c.name}</option>
      ))}
    </select>
  );
};

const CatalogBulkVisibility = ({ catalogCompanyId }) => {
  const [busy, setBusy] = useState(null);
  const run = async (enabled) => {
    const msg = enabled
      ? 'Habilitar no catálogo TODOS os produtos vinculados à filial regente?\n\nProdutos de outras filiais ficarão fora do catálogo.'
      : 'Desabilitar TODOS os produtos do catálogo público?\n\nO catálogo ficará vazio até você habilitar novamente (em massa ou produto a produto).';
    if (!window.confirm(msg)) return;
    setBusy(enabled ? 'on' : 'off');
    try {
      const { data } = await axios.post(`${API}/products/catalog-visibility/bulk`, { enabled }, { withCredentials: true });
      toast.success(enabled
        ? `${data.enabled_count} produto(s) habilitado(s) no catálogo (${data.linked_total} vinculados à filial regente)`
        : `${data.disabled_count} produto(s) removido(s) do catálogo`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar o catálogo');
    } finally { setBusy(null); }
  };
  return (
    <div className="p-3 rounded-xl border border-teal-200 bg-teal-50/40" data-testid="catalog-bulk-visibility">
      <p className="text-xs font-bold text-gray-800 flex items-center gap-1.5"><Eye size={14} className="text-teal-600" /> Visibilidade em massa no catálogo</p>
      <p className="text-[10px] text-gray-500 mt-0.5 mb-2">Liga ou desliga a opção "No catálogo" de todos os produtos de uma só vez. Ao habilitar, entram apenas os produtos vinculados à filial regente.</p>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => run(true)} disabled={!!busy || !catalogCompanyId} className="px-3 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-xs font-bold" data-testid="catalog-bulk-enable">
          {busy === 'on' ? 'Habilitando...' : 'Habilitar todos (filial regente)'}
        </button>
        <button type="button" onClick={() => run(false)} disabled={!!busy} className="px-3 py-1.5 rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50 text-xs font-bold" data-testid="catalog-bulk-disable">
          {busy === 'off' ? 'Desabilitando...' : 'Desabilitar todos'}
        </button>
      </div>
    </div>
  );
};

const PdvSettings = ({ settings, set }) => {
  const [companies, setCompanies] = useState([]);
  useEffect(() => {
    const f = async () => {
      try {
        const { data } = await axios.get(`${API}/erp/companies`, { withCredentials: true });
        setCompanies(data.data || []);
      } catch { /* ERP may not be enabled */ }
    };
    f();
  }, []);
  return (
    <div className={card} data-testid="pdv-settings">
      <h2 className={sectionTitle}>
        <ShoppingCart size={18} className="text-teal-600" /><span>Catalogo como PDV</span>
      </h2>
      <p className="text-xs text-gray-500 mb-5">Quando ativado, cada venda realizada pelo catalogo tambem cria uma venda no sistema de gestao (ERP), atualiza o estoque e registra no financeiro.</p>
      <div className="space-y-4">
        <Toggle checked={settings.catalog_as_pdv} onChange={v => set('catalog_as_pdv', v)} label="Usar catalogo como PDV" description="Vendas do catalogo sao registradas automaticamente no ERP" />
        {settings.catalog_as_pdv && (
          <div>
            <label className={labelClass}>Empresa do PDV *</label>
            <select value={settings.pdv_company_id || ''} onChange={(e) => set('pdv_company_id', e.target.value)} className={inputClass} data-testid="pdv-company-select">
              <option value="">Selecione a empresa</option>
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.code} | {c.name}</option>
              ))}
            </select>
            <p className="text-[10px] text-gray-400 mt-1">As vendas do catalogo serao registradas nesta empresa</p>
            {companies.length === 0 && (
              <p className="text-xs text-amber-600 mt-2">Nenhuma empresa cadastrada. Crie uma empresa no ERP primeiro.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const SettingsManagement = ({ extra = null }) => {
  const [settings, setSettings] = useState({
    store_name: '', logo_url: '', banner_url: '',
    primary_color: '#0A0A0A', secondary_color: '#F3F4F6', accent_color: '#25D366',
    header_bg_color: '#FFFFFF', header_text_color: '#111111',
    card_bg_color: '#FFFFFF', card_text_color: '#111111',
    price_color: '#111111', button_color: '#25D366', button_text_color: '#FFFFFF',
    page_bg_color: '#F3F4F6', category_active_color: '#111111', footer_bg_color: '#FFFFFF',
    grid_mobile: 2, grid_desktop: 4, whatsapp_number: '',
    checkout_email: false, checkout_phone: true, checkout_observation: false, checkout_address: false,
    checkout_pf_enabled: false, checkout_pf_name: false, checkout_pf_cpf: false, checkout_pf_birth: false,
    checkout_pj_enabled: false, checkout_pj_razao: false, checkout_pj_cnpj: false, checkout_pj_ie: false,
    photo_zoom: true, photo_download: false, photo_download_order: false, photo_effect: 'none',
    show_brand: true, show_category: true, show_grade: true, show_kit_qty: true,
    show_unit_price: true, show_kit_price: true, show_reference: true, show_weight: true,
    show_sku: false, show_description: false,
    // iter77de: qual valor rege o destaque grande do card (o outro fica em textual acima).
    // "price" (default, valor principal) | "box_price" (valor por item unitario).
    catalog_main_price: 'price',
    // iter77de: qual filial rege o catalogo publico — produtos/estoque dessa filial + pedidos ali.
    catalog_company_id: '',
    media_format: '1080x1080',
    catalog_as_pdv: false, pdv_company_id: '',
    block_sale_no_stock: false, block_catalog_no_stock: false
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('geral');

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/settings`, { withCredentials: true });
      setSettings(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/settings`, settings, { withCredentials: true });
      toast.success('Configurações salvas!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao salvar'); }
  };

  const set = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-600 border-t-transparent"></div>
    </div>
  );

  const tabs = [
    { id: 'geral', label: 'Geral', icon: Settings },
    { id: 'produto', label: 'Produto', icon: Package },
    { id: 'estoque', label: 'Estoque', icon: Boxes },
    { id: 'checkout', label: 'Checkout', icon: ShoppingCart },
    { id: 'fotos', label: 'Fotos', icon: Camera },
  ];

  return (
    <div data-testid="settings-management">
      <div className="grid grid-cols-5 gap-1 mb-5 bg-gray-100 rounded-xl p-1 overflow-x-auto" data-testid="settings-tabs">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)}
              data-testid={`settings-tab-${tab.id}`}
              className={`py-2.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center space-x-1.5 ${active ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <Icon size={14} /><span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {activeTab === 'geral' && (
          <>
            <div className={card}>
              <h2 className={sectionTitle}>
                <Settings size={18} className="text-teal-600" /><span>Informações da Loja</span>
              </h2>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Nome da Loja *</label>
                  <input type="text" value={settings.store_name} onChange={(e) => set('store_name', e.target.value)} className={inputClass} required />
                </div>
                <ImageUploadField label="Logomarca" field="logo_url" value={settings.logo_url} set={set} spec="Recomendado: 300x100px, PNG transparente, max 5MB" previewClass="h-14 object-contain" />
                <ImageUploadField label="Banner do Catalogo" field="banner_url" value={settings.banner_url} set={set} spec="Recomendado: 1080x400px, JPG/PNG, max 5MB" previewClass="w-full h-28 object-cover rounded-xl" />
              </div>
            </div>

            <div className={card}>
              <h2 className={sectionTitle}>
                <Palette size={18} className="text-teal-600" /><span>Paleta de Cores</span>
              </h2>

              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3">Pagina</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
                <ColorField label="Fundo da Pagina" field="page_bg_color" value={settings.page_bg_color} set={set} />
                <ColorField label="Cor de Destaque" field="accent_color" value={settings.accent_color} set={set} />
              </div>

              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3">Cabecalho</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
                <ColorField label="Fundo Cabecalho" field="header_bg_color" value={settings.header_bg_color} set={set} />
                <ColorField label="Texto Cabecalho" field="header_text_color" value={settings.header_text_color} set={set} />
              </div>

              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3">Card do Produto</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
                <ColorField label="Fundo do Card" field="card_bg_color" value={settings.card_bg_color} set={set} />
                <ColorField label="Texto do Card" field="card_text_color" value={settings.card_text_color} set={set} />
                <ColorField label="Cor do Preco" field="price_color" value={settings.price_color} set={set} />
              </div>

              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3">Botoes e Categorias</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
                <ColorField label="Botao (fundo)" field="button_color" value={settings.button_color} set={set} />
                <ColorField label="Botao (texto)" field="button_text_color" value={settings.button_text_color} set={set} />
                <ColorField label="Categoria Ativa" field="category_active_color" value={settings.category_active_color} set={set} />
              </div>

              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3">Rodape</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <ColorField label="Fundo do Rodape" field="footer_bg_color" value={settings.footer_bg_color} set={set} />
              </div>
            </div>

            <div className={card}>
              <h2 className={sectionTitle}>
                <Grid size={18} className="text-teal-600" /><span>Layout do Catálogo</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Colunas no Mobile</label>
                  <select value={settings.grid_mobile} onChange={(e) => set('grid_mobile', parseInt(e.target.value))} className={inputClass}>
                    <option value="1">1 coluna</option>
                    <option value="2">2 colunas</option>
                    <option value="3">3 colunas</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Colunas no Desktop</label>
                  <select value={settings.grid_desktop} onChange={(e) => set('grid_desktop', parseInt(e.target.value))} className={inputClass}>
                    <option value="2">2 colunas</option>
                    <option value="3">3 colunas</option>
                    <option value="4">4 colunas</option>
                    <option value="5">5 colunas</option>
                    <option value="6">6 colunas</option>
                  </select>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'produto' && (
          <div className={card}>
            {/* iter77de: bloco "Regras do Catalogo" — filial regente + valor principal em destaque */}
            <h2 className={sectionTitle}>
              <Package size={18} className="text-teal-600" /><span>Regras do Catálogo</span>
            </h2>
            <p className="text-xs text-gray-500 mb-4">Definem qual filial rege o catálogo e qual valor aparece em destaque nos cards.</p>
            <div className="space-y-4 mb-6">
              <div>
                <label className={labelClass}>Filial que rege o catálogo *</label>
                <CatalogCompanySelect value={settings.catalog_company_id || ''} onChange={v => set('catalog_company_id', v)} />
                <p className="text-[10px] text-gray-400 mt-1">Somente produtos vinculados a essa filial aparecem no catálogo. Estoque e pedidos também são dessa filial. <strong>Obrigatório</strong> — a primeira filial cadastrada é atribuída automaticamente.</p>
              </div>
              <CatalogBulkVisibility catalogCompanyId={settings.catalog_company_id} />
              <div>
                <label className={labelClass}>Valor principal (destaque no card)</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="catalog-main-price-selector">
                  {[
                    { value: 'price', label: 'Valor', desc: 'Valor principal do cadastro (Aba Básico)' },
                    { value: 'box_price', label: 'Valor por item (unitário)', desc: 'Preço unitário — Aba Outros' },
                  ].map(opt => (
                    <button key={opt.value} type="button" onClick={() => set('catalog_main_price', opt.value)}
                      className={`p-3 rounded-xl border text-left transition-all ${(settings.catalog_main_price || 'price') === opt.value ? 'border-teal-500 bg-teal-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                      data-testid={`main-price-${opt.value}`}>
                      <p className={`text-sm font-bold ${(settings.catalog_main_price || 'price') === opt.value ? 'text-teal-700' : 'text-gray-700'}`}>{opt.label}</p>
                      <p className="text-[10px] text-gray-500">{opt.desc}</p>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-gray-400 mt-1">O outro valor (se preenchido e habilitado no toggle) aparece em texto acima. O selecionado aqui fica em destaque no rodapé do card.</p>
              </div>
            </div>

            <h2 className={sectionTitle}>
              <Eye size={18} className="text-teal-600" /><span>Campos visíveis no site</span>
            </h2>
            <p className="text-xs text-gray-500 mb-4">Marque os campos que devem aparecer no card do produto no catálogo</p>
            <div className="space-y-2 mb-6">
              <Toggle checked={settings.show_brand ?? true} onChange={v => set('show_brand', v)} label="Marca" description="Ex: CACAU KIDS" />
              <Toggle checked={settings.show_category ?? true} onChange={v => set('show_category', v)} label="Categoria" description="Nome da categoria do produto" />
              <Toggle checked={settings.show_grade ?? true} onChange={v => set('show_grade', v)} label="Grade" description="Ex: 01/03 anos" />
              <Toggle checked={settings.show_kit_qty ?? true} onChange={v => set('show_kit_qty', v)} label="Qtd no Kit" description="Ex: Kit com 24 peças" />
              <Toggle checked={settings.show_unit_price ?? true} onChange={v => set('show_unit_price', v)} label="Valor" description="Valor principal do produto (preço de venda)" />
              <Toggle checked={settings.show_kit_price ?? true} onChange={v => set('show_kit_price', v)} label="Valor por item (unitário)" description="Preço unitário por peça — usado em kits" />
              <Toggle checked={settings.show_reference ?? true} onChange={v => set('show_reference', v)} label="Referência" description="Código de referência do fabricante" />
              <Toggle checked={settings.show_weight ?? true} onChange={v => set('show_weight', v)} label="Peso" description="Peso do produto em gramas" />
              <Toggle checked={settings.show_stock ?? false} onChange={v => set('show_stock', v)} label="Estoque" description="Quantidade disponível em estoque" />
              <Toggle checked={settings.show_sku ?? false} onChange={v => set('show_sku', v)} label="SKU" description="Código interno do produto" />
              <Toggle checked={settings.show_description ?? false} onChange={v => set('show_description', v)} label="Descrição" description="Texto descritivo do produto" />
            </div>

            <h2 className={sectionTitle}>
              <Image size={18} className="text-teal-600" /><span>Formato da Mídia</span>
            </h2>
            <p className="text-xs text-gray-500 mb-3">Formato das imagens e vídeos exibidos no catálogo</p>
            <div className="grid grid-cols-3 gap-2" data-testid="media-format-selector">
              {[
                { value: '1080x1080', label: '1:1', desc: 'Quadrado' },
                { value: '1080x1320', label: '4:5', desc: 'Retrato' },
                { value: '1080x1920', label: '9:16', desc: 'Vertical' }
              ].map(fmt => (
                <button key={fmt.value} type="button" onClick={() => set('media_format', fmt.value)}
                  className={`p-3 rounded-xl border text-center transition-all ${settings.media_format === fmt.value ? 'border-teal-500 bg-teal-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                  data-testid={`format-${fmt.value}`}>
                  <div className={`mx-auto mb-2 bg-gray-300 rounded ${fmt.value === '1080x1080' ? 'w-10 h-10' : fmt.value === '1080x1320' ? 'w-10 h-12' : 'w-8 h-14'}`} />
                  <p className={`text-xs font-bold ${settings.media_format === fmt.value ? 'text-teal-700' : 'text-gray-600'}`}>{fmt.label}</p>
                  <p className="text-[10px] text-gray-500">{fmt.desc}</p>
                  <p className="text-[9px] text-gray-400">{fmt.value}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'estoque' && (
          <div className={card} data-testid="estoque-settings">
            <h2 className={sectionTitle}>
              <Boxes size={18} className="text-teal-600" /><span>Controle de Estoque</span>
            </h2>
            <p className="text-xs text-gray-500 mb-4">Configure como o sistema bloqueia vendas e exibicao quando um produto fica sem estoque. Os controles sao independentes para venda interna (PDV/Pedido) e catalogo publico.</p>
            <div className="space-y-2">
              <Toggle
                checked={settings.block_sale_no_stock}
                onChange={v => set('block_sale_no_stock', v)}
                label="Bloquear venda interna sem estoque"
                description="Impede que o PDV finalize uma venda quando o item esta zerado ou quando a quantidade pedida ultrapassa o estoque disponivel"
              />
              <Toggle
                checked={settings.block_catalog_no_stock}
                onChange={v => set('block_catalog_no_stock', v)}
                label="Ocultar produtos sem estoque no catalogo"
                description="Quando ativado, produtos com estoque zerado nao aparecem no catalogo publico e o checkout valida a quantidade disponivel"
              />
            </div>
          </div>
        )}

        {activeTab === 'checkout' && (
          <>
            <div className={card}>
              <h2 className={sectionTitle}>
                <MessageCircle size={18} className="text-teal-600" /><span>WhatsApp para Pedidos</span>
              </h2>
              <div>
                <label className={labelClass}>Número do WhatsApp</label>
                <input type="tel" value={settings.whatsapp_number} onChange={(e) => set('whatsapp_number', e.target.value)} placeholder="(00) 00000-0000" className={inputClass} />
                <p className="text-xs text-gray-500 mt-1.5">Os pedidos serão enviados para este número</p>
              </div>
            </div>

            <div className={card}>
              <h2 className={sectionTitle}><span>Dados do Checkout</span></h2>
              <div className="space-y-2">
                <Toggle checked={settings.checkout_email} onChange={v => set('checkout_email', v)} label="E-mail" />
                <Toggle checked={settings.checkout_phone} onChange={v => set('checkout_phone', v)} label="Telefone (WhatsApp)" />
                <Toggle checked={settings.checkout_observation} onChange={v => set('checkout_observation', v)} label="Observação" />
                <Toggle checked={settings.checkout_address} onChange={v => set('checkout_address', v)} label="Endereço completo" />
              </div>
            </div>

            <div className={card}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-bold text-gray-900">Pessoa Física (PF)</h2>
                <label className="relative cursor-pointer">
                  <input type="checkbox" checked={settings.checkout_pf_enabled} onChange={(e) => set('checkout_pf_enabled', e.target.checked)} className="sr-only peer" />
                  <div className="w-10 h-5 bg-gray-300 rounded-full peer peer-checked:bg-teal-600 peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                </label>
              </div>
              {settings.checkout_pf_enabled && (
                <div className="space-y-2">
                  <Toggle checked={settings.checkout_pf_name} onChange={v => set('checkout_pf_name', v)} label="Nome completo" />
                  <Toggle checked={settings.checkout_pf_cpf} onChange={v => set('checkout_pf_cpf', v)} label="CPF" />
                  <Toggle checked={settings.checkout_pf_birth} onChange={v => set('checkout_pf_birth', v)} label="Data de nascimento" />
                </div>
              )}
            </div>

            <div className={card}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-bold text-gray-900">Pessoa Jurídica (PJ)</h2>
                <label className="relative cursor-pointer">
                  <input type="checkbox" checked={settings.checkout_pj_enabled} onChange={(e) => set('checkout_pj_enabled', e.target.checked)} className="sr-only peer" />
                  <div className="w-10 h-5 bg-gray-300 rounded-full peer peer-checked:bg-teal-600 peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                </label>
              </div>
              {settings.checkout_pj_enabled && (
                <div className="space-y-2">
                  <Toggle checked={settings.checkout_pj_razao} onChange={v => set('checkout_pj_razao', v)} label="Razão Social" />
                  <Toggle checked={settings.checkout_pj_cnpj} onChange={v => set('checkout_pj_cnpj', v)} label="CNPJ" />
                  <Toggle checked={settings.checkout_pj_ie} onChange={v => set('checkout_pj_ie', v)} label="Inscrição Estadual" />
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'fotos' && (
          <div className={card}>
            <h2 className={sectionTitle}>
              <Camera size={18} className="text-teal-600" /><span>Configurações de Fotos</span>
            </h2>
            <div className="space-y-2 mb-4">
              <Toggle checked={settings.photo_zoom} onChange={v => set('photo_zoom', v)} label="Permitir zoom" description="Clique na foto para ampliar" />
              <Toggle checked={settings.photo_download} onChange={v => set('photo_download', v)} label="Download de fotos" description="Botão de download no catálogo" />
              <Toggle checked={settings.photo_download_order} onChange={v => set('photo_download_order', v)} label="Download nos detalhes do pedido" description="Cliente baixa fotos dos produtos" />
            </div>
            <div>
              <label className={labelClass}>Efeito Visual</label>
              <select value={settings.photo_effect} onChange={(e) => set('photo_effect', e.target.value)} className={inputClass}>
                <option value="none">Nenhum</option>
                <option value="grayscale">Preto e Branco</option>
                <option value="sepia">Sépia</option>
                <option value="blur">Desfoque</option>
              </select>
            </div>
          </div>
        )}

        {activeTab === 'pdv' && null}

        <div className="flex gap-3 sticky bottom-0 bg-gradient-to-t from-white via-white to-transparent pt-4 pb-2 -mx-1">
          <button type="submit" data-testid="settings-save-btn"
            className="bg-teal-600 hover:bg-teal-700 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors flex items-center space-x-2 shadow-sm"
          >
            <Save size={16} /><span>Salvar Configurações</span>
          </button>
          <button type="button" onClick={fetchSettings} data-testid="settings-discard-btn"
            className="bg-white border border-gray-200 text-gray-600 px-5 py-2.5 rounded-lg font-semibold text-sm hover:text-gray-900 hover:border-gray-300 flex items-center space-x-2 transition-colors"
          >
            <RotateCcw size={16} /><span>Descartar</span>
          </button>
        </div>
      </form>
      {/* iter156gf: parametros operacionais do Catalogo (antes em Configuracoes > aba Catalogo) */}
      {extra && (
        <div className="mt-8 pt-6 border-t-2 border-dashed border-violet-200" data-testid="catalog-operational-settings">
          <p className="text-[10px] font-black uppercase tracking-widest text-violet-600 mb-3">Parametros operacionais do Catalogo (pedidos, PDV, solicitacoes de compra)</p>
          {extra}
        </div>
      )}
    </div>
  );
};
